import type { OverlayPollResult } from '@pin/schemas/overlay';
import gsap from 'gsap';
import './poll-notification.css';
import { collectRefs, html } from './utils/html';

/**
 * Tag in the top-left corner, on the EBU safe lines. Two are mounted: one
 * shown for a while when a poll opens in the chat, and one below it with the
 * result a moderator has put on air, which stays until it is taken off and
 * whose bars move as the tally does. The result reaches down to where the
 * people in the shot have their heads, so it is the narrower of the two and
 * wraps its text. Modelled on the tag layer of nora-render-sport2026: a black
 * slab wipes in from the left with a lead slab ahead of it, and the text
 * slides into place behind it. The top-right corner is taken: the NRK TV
 * player draws its logo there.
 */
const params = {
  /** Seconds the tag stays on screen before it takes itself out. */
  holdDuration: 8,
  durationIn: 0.45,
  durationOut: 0.45,
  durationLead: 0.4,
  delayLead: 0.1,
  durationModules: 0.6,
  staggerModules: 0.05,
  /** The result bars grow once the rows have slid in. */
  durationBars: 0.8,
  delayBars: 0.3,
  staggerBars: 0.05,
  /** Distance the text travels while it slides in and out. */
  slideIn: 150,
  slideOut: 100,
  contentSlideOut: 32,
  easeSlab: 'expo',
  easeLead: 'power3',
  easeContent: 'power1',
  easeModules: 'expo',
  easeBars: 'power2.out',
  easeOut: 'power3',
  labelText: {
    opened: 'Ny avstemning i chatten',
    result: 'Resultat av avstemningen',
  },
  /**
   * The lead sweeps from the client's green-800 to the accent green, as the
   * lead of the chat message does.
   */
  leadColorStart: '#016630',
  leadColorEnd: '#2dd17d',
};

const percentageText = (percentage: number) => `${Math.round(percentage)} %`;

/** Clipped so nothing of the element is visible, collapsed at its left edge. */
const clipToLeft = 'inset(0% 100% 0% 0%)';
/** Clipped so nothing of the element is visible, collapsed at its right edge. */
const clipToRight = 'inset(0% 0% 0% 100%)';
const unclipped = 'inset(0% 0% 0% 0%)';

/**
 * What the tag says about the poll: that it just opened, or its result. The
 * result lists the alternatives with their share of the votes. Mirrors the
 * OVERLAY_POLL_OPENED and OVERLAY_POLL_RESULT events the overlay receives,
 * less the envelope.
 */
export type PollTag =
  | { variant: 'opened'; question: string }
  | {
      variant: 'result';
      question: string;
      alternatives: NonNullable<OverlayPollResult['result']>['alternatives'];
    };

export interface PollNotificationOptions {
  /**
   * Seconds on screen before the tag takes itself out. `null` keeps it on
   * screen until it is hidden, for the result.
   */
  holdDuration?: number | null;
  /** Below the slot of the opened tag, so the two can be on air together. */
  below?: boolean;
}

/**
 * The same poll, as far as the tag can tell: a new tally for a result on
 * screen changes the bars in place rather than taking the tag out and in.
 */
const samePoll = (shown: PollTag, tag: PollTag): boolean => {
  if (shown.variant !== tag.variant || shown.question !== tag.question) {
    return false;
  }
  if (shown.variant !== 'result' || tag.variant !== 'result') return true;
  return (
    shown.alternatives.length === tag.alternatives.length &&
    shown.alternatives.every((alt, i) => alt.text === tag.alternatives[i]?.text)
  );
};

export class PollNotification {
  private readonly refs = collectRefs(
    html`<div data-ref="frame" class="poll-notification">
      <div class="safe-area">
        <div data-ref="box" class="box">
          <div data-ref="mainSlab" class="main-slab"></div>
          <div data-ref="leadSlab" class="lead-slab"></div>
          <div class="marker"></div>
          <div data-ref="content" class="content">
            <div data-ref="label" class="label">${params.labelText.opened}</div>
            <div data-ref="question" class="question"></div>
            <div data-ref="results" class="results"></div>
          </div>
        </div>
      </div>
    </div>`,
    {
      frame: HTMLDivElement,
      box: HTMLDivElement,
      mainSlab: HTMLDivElement,
      leadSlab: HTMLDivElement,
      content: HTMLDivElement,
      label: HTMLDivElement,
      question: HTMLDivElement,
      results: HTMLDivElement,
    }
  );
  private readonly holdDuration: number | null;
  private pendingTag: PollTag | null | undefined = undefined;
  private isAnimating = false;
  private hold: gsap.core.Tween | undefined;
  /** The tag on screen, or being taken in. */
  private shown: PollTag | null = null;

  constructor({
    holdDuration = params.holdDuration,
    below = false,
  }: PollNotificationOptions = {}) {
    this.holdDuration = holdDuration;
    this.refs.frame.classList.toggle('below', below);

    gsap.set(this.refs.box, this.rest.box);
    gsap.set(this.refs.mainSlab, this.rest.mainSlab);
    gsap.set(this.refs.leadSlab, this.rest.leadSlab);
    gsap.set(this.refs.content, this.rest.content);
    gsap.set(this.modules(), this.rest.modules);
  }

  /**
   * Rest state with nothing visible, from which `take()` starts. The box is
   * hidden with opacity and visibility rather than autoAlpha: reverting an
   * autoAlpha set derives visibility from the opacity it recorded, 0 by then
   * because of the fade in `takeout()`, so the box would stay hidden when
   * the takeout is scrubbed back in GSAP DevTools.
   */
  private readonly rest = {
    box: { opacity: 0, visibility: 'hidden', clipPath: unclipped, x: 0 },
    mainSlab: { clipPath: clipToLeft },
    leadSlab: { clipPath: clipToRight, backgroundColor: params.leadColorEnd },
    content: { clipPath: clipToLeft, x: 0 },
    modules: { x: 0, opacity: 1 },
  };

  public get element(): HTMLDivElement {
    return this.refs.frame;
  }

  /**
   * Show the given tag, or hide the current one when `null`. Calls are
   * coalesced: the latest call wins once the running animation has finished.
   * The same poll with a new tally is updated in place. A shown tag takes
   * itself out after `holdDuration` seconds, unless that is `null`.
   */
  public async animate(tag: PollTag | null): Promise<void> {
    this.pendingTag = tag;

    if (this.isAnimating) return;

    this.isAnimating = true;

    while (this.pendingTag !== undefined) {
      const target = this.pendingTag;
      this.pendingTag = undefined;
      this.hold?.kill();
      this.hold = undefined;

      if (
        target &&
        this.shown &&
        this.isVisible() &&
        samePoll(this.shown, target)
      ) {
        this.shown = target;
        await this.update(target).then();
      } else {
        await this.takeout()?.then();
        this.shown = null;
        if (!target) continue;
        await this.prepare(target);
        await this.take().then();
      }

      if (this.holdDuration != null) {
        this.hold = gsap.delayedCall(this.holdDuration, () => {
          this.hold = undefined;
          void this.animate(null);
        });
      }
    }

    this.isAnimating = false;
  }

  /** Render the tag without animating, so `take()` can be built and seeked. */
  public async prepare(tag: PollTag): Promise<void> {
    this.shown = tag;
    this.refs.label.textContent = params.labelText[tag.variant];
    this.refs.question.textContent = tag.question;
    this.refs.results.replaceChildren();
    this.refs.box.classList.toggle('has-results', tag.variant === 'result');
    if (tag.variant !== 'result') return;

    // The leaders are set in bold: every alternative sharing the top share,
    // none when nobody voted.
    const top = Math.max(0, ...tag.alternatives.map((alt) => alt.percentage));
    for (const alt of tag.alternatives) {
      const share = alt.percentage / 100;
      const leading = top > 0 && alt.percentage === top;
      this.refs.results.append(
        html`<div class="result ${leading ? 'leading' : ''}">
          <div class="result-text">${alt.text}</div>
          <div class="result-percentage">${percentageText(alt.percentage)}</div>
          <div class="result-bar">
            <div class="result-fill" data-share=${share}></div>
          </div>
        </div>`
      );
    }
    gsap.set(this.fills(), { scaleX: 0, transformOrigin: 'left center' });
    gsap.set(this.rows(), this.rest.modules);
  }

  /**
   * A new tally for the result on screen: the shares and the leaders change
   * in place, and each bar grows or shrinks to its new share.
   */
  private update(tag: PollTag): gsap.core.Timeline {
    const timeline = gsap.timeline();
    if (tag.variant !== 'result') return timeline;

    const top = Math.max(0, ...tag.alternatives.map((alt) => alt.percentage));
    const rows = this.rows();
    for (const [i, alt] of tag.alternatives.entries()) {
      const row = rows[i];
      if (row == null) continue;
      const share = alt.percentage / 100;
      row.classList.toggle('leading', top > 0 && alt.percentage === top);
      row.querySelector('.result-percentage')!.textContent = percentageText(
        alt.percentage
      );
      const fill = row.querySelector<HTMLElement>('.result-fill')!;
      fill.dataset.share = String(share);
      timeline.to(
        fill,
        { scaleX: share, duration: params.durationBars, ease: params.easeBars },
        0
      );
    }
    return timeline;
  }

  /** The label, the question and the result rows: what slides in and out. */
  private modules(): HTMLElement[] {
    return [this.refs.label, this.refs.question, ...this.rows()];
  }

  private rows(): HTMLElement[] {
    return Array.from(this.refs.results.children) as HTMLElement[];
  }

  private fills(): HTMLElement[] {
    return Array.from(
      this.refs.results.querySelectorAll<HTMLElement>('.result-fill')
    );
  }

  private isVisible(): boolean {
    return gsap.getProperty(this.refs.box, 'opacity') === 1;
  }

  public take() {
    const timeline = gsap.timeline();
    const modules = this.modules();
    const fills = this.fills();

    timeline
      .set(this.refs.box, { autoAlpha: 1, clipPath: unclipped, x: 0 })
      // The black slab grows out of the left edge.
      .fromTo(
        this.refs.mainSlab,
        { clipPath: clipToLeft },
        {
          clipPath: unclipped,
          duration: params.durationIn,
          ease: params.easeSlab,
        },
        0
      )
      // The lead starts covering the whole box and retreats to the right,
      // cooling from the dark green to the accent green on the way, so it looks
      // like it is sweeping ahead of the slab.
      .fromTo(
        this.refs.leadSlab,
        { clipPath: unclipped, backgroundColor: params.leadColorStart },
        {
          clipPath: clipToRight,
          backgroundColor: params.leadColorEnd,
          duration: params.durationLead,
          ease: params.easeLead,
        },
        params.delayLead
      )
      .fromTo(
        this.refs.content,
        { clipPath: clipToLeft, x: 0 },
        {
          clipPath: unclipped,
          duration: params.durationIn,
          ease: params.easeContent,
        },
        0
      )
      .fromTo(
        modules,
        { opacity: 0, x: params.slideIn },
        {
          opacity: 1,
          x: 0,
          duration: params.durationModules,
          stagger: params.staggerModules,
          ease: params.easeModules,
        },
        0
      );

    if (fills.length > 0) {
      // Each bar grows to its alternative's share of the votes.
      timeline.fromTo(
        fills,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: (_index, fill: HTMLElement) => Number(fill.dataset.share),
          duration: params.durationBars,
          stagger: params.staggerBars,
          ease: params.easeBars,
        },
        params.delayBars
      );
    }

    return timeline;
  }

  public takeout() {
    if (!this.isVisible()) return;

    const timeline = gsap.timeline();
    const modules = this.modules();

    return (
      timeline
        .to(
          modules,
          {
            x: params.slideOut,
            opacity: 0,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(
          this.refs.content,
          {
            x: params.contentSlideOut,
            clipPath: clipToRight,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(
          this.refs.box,
          {
            clipPath: clipToRight,
            duration: params.durationOut,
            ease: params.easeOut,
          },
          0
        )
        .to(this.refs.box, { opacity: 0, duration: params.durationOut }, 0.1)
        // Back to rest as zero-duration tweens, so the timeline can be
        // scrubbed backwards in GSAP DevTools.
        .set(this.refs.box, this.rest.box)
        .set(this.refs.mainSlab, this.rest.mainSlab)
        .set(this.refs.leadSlab, this.rest.leadSlab)
        .set(this.refs.content, this.rest.content)
        .set(modules, this.rest.modules)
    );
  }
}
